import React, { useState, useCallback } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapMaterialMaster } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import { Plus, Search } from 'lucide-react';
import { SapMaterialFormModal } from '@/components/sap/SapMaterialFormModal';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

export const SapMaterialsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      return mockSapSimulatorService.getMaterials({
        search,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
    },
    [search]
  );

  const { items: materials, isLoading, hasNextPage, isFetchingNextPage, total, sentinelRef, retry } = useInfiniteScroll<SapMaterialMaster>({
    fetchFn,
    pageSize: 25,
  });

  const handleCreateMaterial = async (payload: Omit<SapMaterialMaster, 'id' | 'createdAt' | 'updatedAt'>) => {
    await mockSapSimulatorService.createMaterial(payload);
    retry();
  };

  const columns: ColumnDef<SapMaterialMaster>[] = [
    {
      header: 'Material Code',
      accessorKey: 'materialCode',
      cell: (row) => <span className="font-mono font-bold text-primary">{row.materialCode}</span>,
    },
    {
      header: 'Description',
      accessorKey: 'description',
      cell: (row) => <span className="font-bold text-foreground">{row.description}</span>,
    },
    {
      header: 'Category',
      accessorKey: 'category',
      cell: (row) => <span className="text-xs font-semibold text-foreground-secondary">{row.category}</span>,
    },
    {
      header: 'UOM',
      accessorKey: 'uom',
      cell: (row) => <span className="font-mono text-xs text-foreground-muted">{row.uom}</span>,
    },
    {
      header: 'Std Price',
      accessorKey: 'standardPrice',
      cell: (row) => <span className="font-mono font-bold text-foreground">{formatCurrency(row.standardPrice, row.currency)}</span>,
    },
    {
      header: 'Plant',
      accessorKey: 'plantCode',
      cell: (row) => <span className="font-mono text-xs font-semibold text-foreground-secondary">{row.plantCode}</span>,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <span
          className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
            row.status === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
          }`}
        >
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-4">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border border-border bg-card shadow-2xs font-heading">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search material code, description, plant..."
              className="w-full h-9 pl-10 pr-4 text-xs font-medium rounded-xl border border-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <Button size="sm" onClick={() => setIsModalOpen(true)} className="rounded-xl font-bold">
            <Plus className="h-4 w-4 mr-1.5" /> Create Material
          </Button>
        </div>

        {/* Table */}
        <DataTable
          data={materials}
          columns={columns}
          isLoading={isLoading}
          pageSize={25}
          totalCount={total}
          infiniteScroll={true}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          sentinelRef={sentinelRef}
        />

        {/* Modal */}
        <SapMaterialFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateMaterial}
        />
      </div>
    </SAPLayout>
  );
};
